import { useState } from 'react'
import { AIAvatar } from '@/components/ui/AIAvatar'
import { Button } from '@/components/ui/Button'
import { login, signup, setToken, type User } from '@/api/auth'
import { SocialButtons } from './SocialButtons'

const DEMO_EMAIL = 'alex.rivera@gmail.com'
const DEMO_PASSWORD = 'Sara!Health2026'

export function Login({ onAuth }: { onAuth: (u: User) => void }) {
  const [mode, setMode] = useState<'login' | 'signup'>('login')
  const [name, setName] = useState('')
  const [email, setEmail] = useState(DEMO_EMAIL)
  const [password, setPassword] = useState(DEMO_PASSWORD)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const isSignup = mode === 'signup'

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setError(null)
    const { ok, data } = isSignup
      ? await signup(email, password, name)
      : await login(email, password)
    setBusy(false)
    if (ok && data.token) { setToken(data.token); onAuth(data.user) }
    else setError(data.detail || 'Something went wrong.')
  }

  const swap = (m: 'login' | 'signup') => {
    setMode(m); setError(null)
    if (m === 'signup') { setEmail(''); setPassword(''); setName('') }
    else { setEmail(DEMO_EMAIL); setPassword(DEMO_PASSWORD) }
  }

  return (
    <div className="flex h-full flex-col items-center justify-center overflow-y-auto bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center text-center">
          <span className="sara-star"><AIAvatar size="lg" /></span>
          <h1 className="mt-3 text-2xl font-semibold text-foreground">SARA</h1>
          <p className="text-sm text-muted-foreground">Your personal health coach · she knows you</p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h2 className="mb-4 text-center text-base font-semibold text-foreground">
            {isSignup ? 'Create your account' : 'Welcome back'}
          </h2>
          <form onSubmit={submit} className="space-y-3">
            {isSignup && <Field label="Name" type="text" value={name} onChange={setName} placeholder="Alex Rivera" />}
            <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" />
            <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" />
            {error && <p className="text-xs text-destructive-text">{error}</p>}
            <Button type="submit" variant="primary" className="mt-1 w-full" disabled={busy}>
              {busy ? 'Please wait…' : isSignup ? 'Create account' : 'Sign in'}
            </Button>
          </form>

          <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
          </div>

          <SocialButtons onAuth={onAuth} />

          <p className="mt-5 text-center text-xs text-muted-foreground">
            {isSignup ? (
              <>Already have an account?{' '}
                <button onClick={() => swap('login')} className="font-medium text-foreground hover:underline">Sign in</button></>
            ) : (
              <>New here?{' '}
                <button onClick={() => swap('signup')} className="font-medium text-foreground hover:underline">Create an account</button></>
            )}
          </p>
        </div>

        <p className="mt-5 text-center text-[11px] leading-relaxed text-muted-foreground">
          {!isSignup && <>Demo account prefilled · {DEMO_EMAIL}<br /></>}
          SARA provides coaching and navigation, not diagnosis.
        </p>
      </div>
    </div>
  )
}

function Field({
  label, type, value, onChange, placeholder,
}: {
  label: string; type: string; value: string; onChange: (v: string) => void; placeholder?: string
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-foreground">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
      />
    </label>
  )
}
